import { Image } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, Link, notFound, redirect } from '@tanstack/react-router'
import { allDocs } from 'content-collections'
import { StatusCodes } from 'http-status-codes'
import type { ComponentPropsWithoutRef } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeSlug from 'rehype-slug'
import remarkGfm from 'remark-gfm'

import { helpcenterSlugPageHeadFromLoaderData } from './$.seo'
import './helpcenter.css'

const LEADING_SLASH = /^\//
const LEADING_RELATIVE = /^\.\//
const TRAILING_SLASH = /\/$/
const HELPCENTER_HREF = /^\/helpcenter(?:\/(.*))?$/

function resolveDocAssetSrc(docMetaPath: string, src: string): string {
  if (src.startsWith('/') || src.startsWith('http://') || src.startsWith('https://')) {
    return src
  }
  const normalizedSrc = src.replace(LEADING_RELATIVE, '')
  const parentDir = docMetaPath.includes('/') ? docMetaPath.split('/').slice(0, -1).join('/') : ''
  return parentDir ? `/docs/${parentDir}/${normalizedSrc}` : `/docs/${normalizedSrc}`
}

export const Route = createFileRoute('/(public)/(main-layout)/helpcenter/$')({
  component: DocPage,
  loader: ({ params }) => {
    const slug = params._splat ?? ''

    if (slug.startsWith('docs/') || slug === 'docs') {
      const rest = slug.slice('docs'.length).replace(LEADING_SLASH, '')
      throw redirect({
        to: rest ? `/helpcenter/${rest}` : '/helpcenter',
        statusCode: StatusCodes.MOVED_PERMANENTLY,
      })
    }

    if (slug === '' || slug === '/') {
      const introDoc = allDocs.find((d) => d._meta.path === 'intro')
      if (introDoc) {
        throw redirect({ to: '/helpcenter/intro' })
      }
    }

    const doc = allDocs.find((d) => {
      return d._meta.path === slug
    })

    if (!doc) {
      throw notFound()
    }

    return { doc }
  },
  head: ({ loaderData, params }) =>
    helpcenterSlugPageHeadFromLoaderData({ doc: loaderData?.doc, slug: params._splat ?? '' }),
})

function DocPage() {
  const { doc } = Route.useLoaderData()

  return (
    <div className="flex flex-col gap-6">
      {doc.title ? <h1 className="font-semibold text-3xl text-foreground tracking-tight">{doc.title}</h1> : null}
      <article className="docs-content">
        <ReactMarkdown
          components={{
            a: ({ href, children, ...props }) => {
              const match = href?.match(HELPCENTER_HREF)
              if (match) {
                const splat = match[1]?.replace(TRAILING_SLASH, '')
                const className = (props as ComponentPropsWithoutRef<'a'>).className
                if (!splat) {
                  return (
                    <Link className={className} to="/helpcenter">
                      {children}
                    </Link>
                  )
                }
                return (
                  <Link className={className} params={{ _splat: splat } as never} to="/helpcenter/$">
                    {children}
                  </Link>
                )
              }
              const anchorProps = props as ComponentPropsWithoutRef<'a'>
              return (
                <a href={href} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined} {...anchorProps}>
                  {children}
                </a>
              )
            },
            img: ({ src, alt, ...props }: { src?: string; alt?: string; [key: string]: unknown }) => {
              if (!src) return null
              const imageSrc =
                src.startsWith('/') || src.startsWith('http') ? src : resolveDocAssetSrc(doc._meta.path, src)
              const { className: imgClassName, ...restImg } = props as ComponentPropsWithoutRef<'img'>
              return <Image alt={alt || ''} className={cn('max-w-full', imgClassName)} src={imageSrc} {...restImg} />
            },
          }}
          rehypePlugins={[rehypeSlug]}
          remarkPlugins={[remarkGfm]}
        >
          {doc.content}
        </ReactMarkdown>
      </article>
    </div>
  )
}
